import { Button, ButtonGroup, ColSplit, elHasSectionMargin, elMt8, Grid, InputGroup } from '@reapit/elements'
import { ICountryOption, IRegionOption, SelectRegions } from '@reapitform/dashboard-ui'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { usePlatformUpdate } from '@reapitform/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { IAgentSettingsData } from '../../../../../api/agent-settings'
import { SelectOptionValue } from '@reapitform/common-ui'
import { SelectCountry } from '@reapitform/dashboard-ui'
import { PhoneNumberCountryCode, strictInput, yup } from '@reapitform/utils'
import { ObjectSchema } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { parsePhoneNumber } from 'libphonenumber-js/max'

export interface IContactDetailsEditFormProps {
  onSuccess?: () => void
  onClose?: () => void
  onFailure?: () => void
  defaultData?: IAgentSettingsData['contactDetails']
  queryKey?: string
}

export const agentContactDetailsValidationSchema = yup.object().shape({
  countryCode: yup.string().optional(),
  phone: yup
    .string()
    .required('Enter the phone number')
    .phoneNumber((ctx) => ctx.parent.countryCode),
  facsimile: yup
    .string()
    .optional()
    .default('')
    .facsimile((ctx) => ctx.parent.countryCode),
  email: yup.string().email('Enter a valid email address').required('Enter a valid email address'),
  emailCc: yup.string().optional().default('').multipleEmail(),
  propertyAddress: yup.object().shape({
    unitNo: yup.string().optional().default(''),
    streetNo: yup.string().optional().default(''),
    street: yup.string().required('Enter the street name'),
    streetType: yup.string().optional().default(''),
    suburb: yup.string().required('Enter the suburb name'),
    state: yup.string().required('Select the state name'),
    postCode: yup.string().postcode().required('Enter the postcode'),
    country: yup.string().required('Select the country'),
  }),
}) as ObjectSchema<IAgentSettingsData['contactDetails'] & { countryCode: PhoneNumberCountryCode }, any, any, ''>

const ContactDetailsEditForm: React.FC<IContactDetailsEditFormProps> = ({
  onClose,
  onSuccess,
  defaultData,
  queryKey,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<SelectOptionValue<ICountryOption>>()
  const [selectedState, setSelectedState] = useState<SelectOptionValue<IRegionOption>>()
  const queryClient = useQueryClient()
  const {
    handleSubmit,
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<IAgentSettingsData['contactDetails'] & { countryCode: PhoneNumberCountryCode }>({
    defaultValues: defaultData,
    resolver: yupResolver(agentContactDetailsValidationSchema),
  })

  const [submitRequest, isSubmitLoading] = usePlatformUpdate<
    IAgentSettingsData['contactDetails'],
    IAgentSettingsData['contactDetails']
  >({
    path: '/api/Organization/ContactDetails',
    method: 'PUT',
    headers: {},
    successMessage: 'Your contact details information successfully updated!',
    errorMessage: 'Oopps... Failed to update your contact details information!',
  })

  const handleFormSubmission = async (
    data: IAgentSettingsData['contactDetails'] & { countryCode: PhoneNumberCountryCode },
  ) => {
    const { countryCode, ...restData } = data

    const payload: IAgentSettingsData['contactDetails'] = {
      ...restData,
      phone: parsePhoneNumber(restData.phone, countryCode).formatInternational(),
    }

    if (restData.facsimile) payload.facsimile = parsePhoneNumber(restData.facsimile, countryCode).formatInternational()

    const result = await submitRequest(payload)
    if (!result) return

    if (queryKey) {
      queryClient.setQueryData([queryKey], result)
      queryClient.invalidateQueries({ queryKey: [queryKey] })
    }
    onSuccess?.()
  }

  function handleChangeState(option: SelectOptionValue<IRegionOption>): void {
    setSelectedState(option)
    setValue('propertyAddress.state', option?.name ?? '')
  }

  function handleChangeCountry(option: SelectOptionValue<ICountryOption>): void {
    setSelectedCountry(option)
    setValue('countryCode', option?.displayShortName)
    setValue('propertyAddress.country', option?.name ?? '')
  }

  useEffect(() => {
    if (!selectedCountry || !selectedState) return
    if (selectedCountry.name === selectedState.country) return

    setSelectedState(undefined)
    setValue('propertyAddress.state', '')
  }, [selectedCountry, selectedState])

  return (
    <form onSubmit={handleSubmit(handleFormSubmission)}>
      <Grid className={elHasSectionMargin}>
        <ColSplit>
          <InputGroup
            label="Phone *"
            type="text"
            {...register('phone')}
            errorMessage={errors.phone?.message}
            {...strictInput(/^[0-9+() ]*$/)}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="Facsimile"
            type="text"
            {...register('facsimile')}
            errorMessage={errors.facsimile?.message}
            {...strictInput(/^[0-9+() ]*$/)}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup label="Office Email *" type="text" {...register('email')} errorMessage={errors.email?.message} />
        </ColSplit>
        <ColSplit>
          <InputGroup label="CC Email" type="text" {...register('emailCc')} errorMessage={errors.emailCc?.message} />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="Unit Number"
            type="text"
            {...register('propertyAddress.unitNo')}
            errorMessage={errors.propertyAddress?.unitNo?.message}
            maxLength={20}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="Street Number"
            type="text"
            {...register('propertyAddress.streetNo')}
            errorMessage={errors.propertyAddress?.streetNo?.message}
            maxLength={20}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="Street Name *"
            type="text"
            {...register('propertyAddress.street')}
            errorMessage={errors.propertyAddress?.street?.message}
            maxLength={100}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="Street Type"
            type="text"
            {...register('propertyAddress.streetType')}
            errorMessage={errors.propertyAddress?.streetType?.message}
            maxLength={100}
          />
        </ColSplit>
        <ColSplit>
          <SelectCountry
            keyValue="name"
            label="Country *"
            errorMessage={errors.propertyAddress?.country?.message}
            value={selectedCountry?.name ?? getValues('propertyAddress.country')}
            onChange={handleChangeCountry}
          />
        </ColSplit>
        <ColSplit>
          <SelectRegions
            keyValue="name"
            label="State *"
            errorMessage={errors.propertyAddress?.state?.message}
            country={(selectedCountry?.value ?? 0).toString()}
            disabled={!selectedCountry?.value}
            value={selectedState?.name ?? getValues('propertyAddress.state')}
            onChange={handleChangeState}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="Suburb *"
            type="text"
            {...register('propertyAddress.suburb')}
            errorMessage={errors.propertyAddress?.suburb?.message}
            maxLength={50}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="Postcode *"
            type="text"
            {...register('propertyAddress.postCode')}
            errorMessage={errors.propertyAddress?.postCode?.message}
            {...strictInput(/^[0-9]*$/)}
          />
        </ColSplit>
      </Grid>
      <ButtonGroup alignment="right" className={elMt8}>
        <Button intent="neutral" onClick={onClose} disabled={isSubmitLoading}>
          Cancel
        </Button>
        <Button intent="primary" loading={isSubmitLoading} disabled={isSubmitLoading}>
          Save
        </Button>
      </ButtonGroup>
    </form>
  )
}

export default ContactDetailsEditForm
